import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

export interface MerchantSummaryRow {
  merchantId: string;
  code: string;
  name: string;
  status: string;
  deposit: number;
  topup: number;
  withdraw: number;
  settle: number;
  payinVolume: number;
  payoutVolume: number;
  payinFee: number;
  payoutFee: number;
  feeSeller: number;
}

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Per-merchant volume + fee summary. Shared by the seller dashboard and the
 * admin seller-detail "Summary" tab. Fee columns reflect the seller's fee
 * percents (payin = deposit + topup, payout = withdraw + settle).
 */
export function MerchantSummaryTable({
  rows,
  payinFeePercent,
  payoutFeePercent,
  isLoading = false,
}: {
  rows: MerchantSummaryRow[];
  payinFeePercent: number;
  payoutFeePercent: number;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">{t('common.loading')}</div>;
  }
  if (rows.length === 0) {
    return <div className="p-4 text-sm text-gray-400">{t('common.noData')}</div>;
  }

  const totals = rows.reduce(
    (acc, r) => ({
      deposit: acc.deposit + r.deposit,
      topup: acc.topup + r.topup,
      withdraw: acc.withdraw + r.withdraw,
      settle: acc.settle + r.settle,
      payinFee: acc.payinFee + r.payinFee,
      payoutFee: acc.payoutFee + r.payoutFee,
    }),
    { deposit: 0, topup: 0, withdraw: 0, settle: 0, payinFee: 0, payoutFee: 0 },
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('merchants.code')}</TableHead>
          <TableHead>{t('merchants.name')}</TableHead>
          <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
          <TableHead className="text-right">{t('transactions.topup')}</TableHead>
          <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
          <TableHead className="text-right">{t('transactions.settle')}</TableHead>
          <TableHead className="text-right">
            {t('transactions.calcPayinFee')} ({payinFeePercent}%)
          </TableHead>
          <TableHead className="text-right">
            {t('transactions.calcPayoutFee')} ({payoutFeePercent}%)
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.merchantId}>
            <TableCell className="font-mono text-xs">{r.code}</TableCell>
            <TableCell>{r.name}</TableCell>
            <TableCell className="text-right">{fmt(r.deposit)}</TableCell>
            <TableCell className="text-right">{fmt(r.topup)}</TableCell>
            <TableCell className="text-right">{fmt(r.withdraw)}</TableCell>
            <TableCell className="text-right">{fmt(r.settle)}</TableCell>
            <TableCell className="text-right">{fmt(r.payinFee)}</TableCell>
            <TableCell className="text-right">{fmt(r.payoutFee)}</TableCell>
          </TableRow>
        ))}
        <TableRow className="font-semibold">
          <TableCell>{t('common.total')}</TableCell>
          <TableCell>{''}</TableCell>
          <TableCell className="text-right">{fmt(totals.deposit)}</TableCell>
          <TableCell className="text-right">{fmt(totals.topup)}</TableCell>
          <TableCell className="text-right">{fmt(totals.withdraw)}</TableCell>
          <TableCell className="text-right">{fmt(totals.settle)}</TableCell>
          <TableCell className="text-right">{fmt(totals.payinFee)}</TableCell>
          <TableCell className="text-right">{fmt(totals.payoutFee)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
